import { useContext } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { GapBox } from 'components/Box'
import { TransferList, useResetFormFieldStateOnFetch } from 'components/Form'
import { SectionHeader } from 'components/Header'
import { Link } from 'components/Link'
import Loading from 'components/Loading'
import Paper from 'components/Paper'
import { PAGE_ARCHITECTURE_KNOWLEDGE_BASE_URL } from 'features/inventory/page/constants'
import { AddEditPageContext } from 'features/inventory/page/contexts/AddEditPageContext'
import { useAbility } from 'providers/casl'
import { FormVariant } from 'types/formVariant'
import { useGetAdUnitsForPageTransferList } from '../hooks/useGetAdUnitsForPageTransferList'
import { CommonSchema } from '../schemas/commonSchema'

type Props = {
  variant: FormVariant
}

export const TransferListSection = ({ variant }: Props): React.JSX.Element => {
  const ability = useAbility()
  const { t } = useTranslation(['features/page', 'common'])

  const { control, watch } = useFormContext<CommonSchema>()
  const { domainId } = watch()

  const { loading: isContextLoading } = useContext(AddEditPageContext)
  const { domainAdUnits, isFetched, isLoading } = useGetAdUnitsForPageTransferList({ domainId })

  useResetFormFieldStateOnFetch<CommonSchema>('adUnitIds', isFetched)

  const isDisabled: boolean = ability.cannot(variant === 'add' ? 'create' : 'update', 'Page')

  if (isContextLoading || isLoading) {
    return <Loading />
  } else if (domainId.length === 0) {
    return <></>
  }

  return (
    <Paper>
      <SectionHeader
        subtitle={t('form.general.adUnitConnection.subtitle')}
        subtitleEndAdornment={
          <Link
            newTab
            to={PAGE_ARCHITECTURE_KNOWLEDGE_BASE_URL}
          >
            {t('common:learnMore')}
          </Link>
        }
        title={t('form.general.adUnitConnection.title')}
      />

      <GapBox />

      <TransferList<CommonSchema>
        all={domainAdUnits}
        control={control}
        disabled={isDisabled}
        name='adUnitIds'
      />
    </Paper>
  )
}
